import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { MonitorSmartphone, Server } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { leases as leaseApi, nodes as api } from '@/api/endpoints'
import { Ago, Duration } from '@/components/Ago'
import { Doing } from '@/components/Doing'
import { Err } from '@/components/Err'
import { ExpertLine } from '@/components/Expert'
import { Facts, type FactRow } from '@/components/Facts'
import { StatusBadge } from '@/components/StatusBadge'
import { NodeRemoveBadge } from './NodeRemoveBadge'
import { ToneBadge } from '@/components/ToneBadge'
import { Crumb, Empty, PageHead, Section } from '@/components/layout/Page'
import { IommuWarning } from '@/components/IommuWarning'
import { ClusterDecisionDialog, type DecisionMode } from './ClusterDecisionDialog'
import { NodeUsageCharts } from './NodeUsageCharts'
import { NodeOnboardRow } from './NodeOnboardRow'
import { NodeRebootChip } from './NodeRebootChip'
import { NodeShellChip } from './NodeShellDialog'
import { RootPasswordChip } from './RootPasswordChip'
import { ACTION as CHIP_ACTION, ACTION_DANGER, CHIP } from './vm/chips'
import { gpuSlot, type GpuLine } from './nodeUsage'
import { useExpert } from '@/hooks/useExpert'
import { usePoll } from '@/hooks/usePoll'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { leaseChanged } from '@/hooks/usePage'
import { useRefreshAfterWrites } from '@/hooks/useWorld'
import { isLive, leaseToneOf, leaseWord, vmSpecText, vmWord, vmToneOf } from '@/domain/lease'
import { ownerLabel } from '@/domain/owner'
import { clusterOriginWord, liveText } from '@/domain/cluster'
import {
  bootEndedTone,
  bootEndedWord,
  clusterNetFaultWord,
  gpuBindWord,
  verifyRows,
  verifyStepWord,
  gpuWord,
  halted,
  imageCopyText,
  membershipWord,
  nodeOriginWord,
  STAGES,
  stage,
  unreportedWord,
  validationCheckDescriptionWord,
  validationCheckWord,
  validationObservedWord,
  validationScopeWord,
  validationStepWord,
} from '@/domain/node'
import { diskText, moveTier, tierText, tierWord } from '@/domain/storage'
import { DASH, exact, size, sizeMb, sizeUsage } from '@/ui/format'
import type { InstallerLine, InstallerRun, Lease, NodeGpuTest, Vm } from '@/api/types'
import type {
  ApiNode,
  MaintenanceResume,
  NodeBootLog,
  NodeClusterDecision,
  NodeCrashRecord,
  NodeValidation,
  NodeValidationAttempt,
  StorageTier,
} from '@/api/types'
import type { World } from './model'

const CAPACITY_POLL_MS = 30000
// 노드 위의 VM. 변경 알림이 다시 읽고(최고 관리자만 이 화면을 연다) 그 사이는 목록과 같은 간격이 메운다.
const HELD_POLL_MS = 8000
// 행 안의 조작 버튼은 그래프의 기간 토글과 같은 크기다.
const ACTION = 'h-7 px-2 text-[12px]'
const VALIDATION_POLL_MS = 10000
const MEMBERSHIP_POLL_MS = 10000
const GPU_BADGE = {
  ok: 'rounded-full border-ok/35 bg-ok/10 font-normal text-ok-ink',
  danger: 'rounded-full border-danger/35 bg-danger/10 font-normal text-danger-ink',
  muted: 'rounded-full font-normal text-muted-foreground',
} as const

// 한 노드의 현재: 상태 · 장비 점검 · VM · 자원 · GPU. 값이 없으면 대시다.

// 진행 줄: 여섯 걸음을 한 줄로 놓고 지금 것을 굵게 한다. 퍼센트는 두지 않는다 —
// 사람이 정하는 것은 "기다릴까, 가 볼까" 이고 그 답은 어느 걸음인지가 준다.
// 어디인지는 `stage` 하나가 답한다 — 걸음 기록이 있으면 그것이고, 없는 구간(검증)은 상태다.
// 서비스에 들어온 노드와 국면을 말할 수 없는 노드에는 줄이 서지 않는다.
function progressTrail(node: ApiNode): [string, ReactNode] | null {
  const here = stage(node)
  const at = STAGES.indexOf(here as (typeof STAGES)[number])
  if (at < 0 || here === '사용 가능') return null
  const progress = node.progress
  const stuck = halted(progress)
  // 설치는 끝났는데 관리 주소로 닿지 않는다(#489). 원인은 추측하지 않는다 — 주소와 사실 하나.
  const unreachable = progress?.state === 'unreachable'
  return [
    '진행',
    <div key="progress" className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {STAGES.map((word, index) => (
          <span
            key={word}
            className={
              index === at
                ? stuck
                  ? 'font-medium text-danger-ink'
                  : 'font-medium'
                : index < at
                  ? 'text-muted-foreground'
                  : 'text-muted-foreground/50'
            }
          >
            {word}
          </span>
        ))}
      </div>
      {progress ? (
        <div className={`mt-1 text-[12px] ${stuck ? 'text-danger-ink' : 'text-muted-foreground'}`}>
          <Doing progress={progress} />
        </div>
      ) : null}
      {unreachable ? (
        <div className="mt-1.5 text-muted-foreground">{node.mgmt_ip ? `${node.mgmt_ip} 로 닿지 않습니다.` : '관리 주소로 닿지 않습니다.'}</div>
      ) : stuck ? (
        <div className="mt-1.5 grid gap-0.5 text-muted-foreground">
          <div>
            {here} 단계에서 문제가 생겼습니다.
            {progress?.item ? ` 문제로 추정되는 장치는 ${progress.item} 입니다.` : ''}
          </div>
          <div>다시 진행하려면 장비를 다시 켜십시오.</div>
        </div>
      ) : null}
    </div>,
  ]
}

// 지난 부팅 한 줄(A6-16): 판정 낱말과 마지막으로 살아 있던 시각. 갑자기 멈춘 부팅만
// 펼칠 거리가 있다 — 정상 종료한 부팅의 마지막 줄은 아무것도 설명하지 않는다.
function BootLogRow({ log }: { log: NodeBootLog }) {
  const [open, setOpen] = useState(false)
  const evidence = [log.errors, log.tail].filter((t) => (t ?? '').trim() !== '')
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <ToneBadge tone={bootEndedTone(log.ended)}>{bootEndedWord(log.ended)}</ToneBadge>
        {log.last_at ? <span className="text-muted-foreground">{exact(log.last_at)}</span> : null}
        {evidence.length > 0 ? (
          <button className="text-[12px] text-muted-foreground underline-offset-2 hover:underline" onClick={() => setOpen((v) => !v)}>
            {open ? '접기' : '기록 보기'}
          </button>
        ) : null}
      </div>
      {open ? (
        <pre className="mt-1.5 max-h-72 overflow-auto rounded border bg-surface-2 p-2 font-mono text-[11px] whitespace-pre-wrap">
          {evidence.join('\n\n')}
        </pre>
      ) : null}
    </div>
  )
}

// 크래시 기록 한 줄(#412): 펌웨어가 남긴 커널 덤프의 요약 한 줄과 도착 시각. 누르면 전문.
// 재설치가 노드 위의 사본을 지우므로 이 기록이 남는 유일한 사본이다.
function CrashRecordRow({ record }: { record: NodeCrashRecord }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {record.created_at ? <span className="text-muted-foreground">{exact(record.created_at)}</span> : null}
        <button className="text-[12px] text-muted-foreground underline-offset-2 hover:underline" onClick={() => setOpen((v) => !v)}>
          {open ? '접기' : '기록 보기'}
        </button>
      </div>
      {record.summary ? <div className="mt-0.5 truncate font-mono text-[11px]">{record.summary}</div> : null}
      {open ? (
        <pre className="mt-1.5 max-h-72 overflow-auto rounded border bg-surface-2 p-2 font-mono text-[11px] whitespace-pre-wrap">{record.body}</pre>
      ) : null}
    </div>
  )
}

/** 설치 한 건(설치기 부팅 한 번, #445): 언제 · 몇 줄 · 마지막 줄. 누르면 그 건의 줄을 모두 읽는다. */
function InstallerRunRow({ node, run }: { node: string; run: InstallerRun }) {
  const [open, setOpen] = useState(false)
  const [lines, setLines] = useState<InstallerLine[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const toggle = () => {
    setOpen((v) => !v)
    if (lines === null) {
      api.installerRun(node, run.installer_boot_id).then(setLines, setError)
    }
  }
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-muted-foreground">{exact(run.started_at)}</span>
        <span>{`${run.lines}줄`}</span>
        {typeof run.percent === 'number' ? <span>{`${run.percent}%`}</span> : null}
        <button className="text-[12px] text-muted-foreground underline-offset-2 hover:underline" onClick={toggle}>
          {open ? '접기' : '줄 보기'}
        </button>
      </div>
      {run.last_line ? <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">{run.last_line}</div> : null}
      {open ? (
        <>
          <Err error={error} />
          <pre className="mt-1.5 max-h-72 overflow-auto rounded border bg-surface-2 p-2 font-mono text-[11px] whitespace-pre-wrap">
            {lines === null ? '…' : lines.map((l) => l.text).join('\n')}
          </pre>
        </>
      ) : null}
    </div>
  )
}

export default function NodeView({ world }: { world: World }) {
  const { id } = useParams()
  // 경로의 형제 이동은 경로만 바꾸고 화면은 남는다 — 앞 노드의 읽기(소속이 실은 화면 이름·점검·용량)와 고른 것·바꾼 이름이
  // 새 노드에 서지 않게 노드마다 새로 그린다.
  return <NodeDetail key={id} id={id} world={world} />
}

function NodeDetail({ id, world }: { id: string | undefined; world: World }) {
  const navigate = useNavigate()
  const row = world.nodes.find((r) => r.node.id === id)
  const expert = useExpert()
  const capacity = usePoll(
    () => (id && expert.on ? api.capacity(id) : Promise.resolve(null)),
    CAPACITY_POLL_MS,
    [id, expert.on],
  )
  // 디스크·데이터 풀은 우리 행이라 바뀌는 일이 드물다 — 전문가가 켜졌을 때 한 번 읽고 용량과 같은 주기로 다시 읽는다.
  const disks = usePoll(
    () => (id && expert.on ? api.disks(id) : Promise.resolve(null)),
    CAPACITY_POLL_MS,
    [id, expert.on],
  )
  // 지난 부팅들(A6-16): 왜 기계가 멈췄는지 묻는 사람의 것이라 고급에서만 읽는다. 글이 크고 묻는 일이 드물다.
  const boots = usePoll(
    () => (id && expert.on ? api.bootLogs(id) : Promise.resolve(null)),
    CAPACITY_POLL_MS,
    [id, expert.on],
  )
  // 크래시 기록(#412): 펌웨어가 남긴 커널 덤프. 부팅 기록과 같은 이유로 고급에서만 읽는다.
  const crashes = usePoll(
    () => (id && expert.on ? api.crashRecords(id) : Promise.resolve(null)),
    CAPACITY_POLL_MS,
    [id, expert.on],
  )
  // 관리 이미지 사본(#331): 새 레시피가 노드마다 퍼지는 것을 보는 사람의 것이라 고급에서만 읽는다. 상세에만 있는 값이다.
  const detail = usePoll(
    () => (id && expert.on ? api.get(id) : Promise.resolve(null)),
    CAPACITY_POLL_MS,
    [id, expert.on],
  )
  // 설치 기록(#445): 설치기가 남긴 줄. 설치가 왜 멈췄는지 묻는 사람의 것이라 고급에서만 읽는다.
  const installs = usePoll(
    () => (id && expert.on ? api.installerRuns(id) : Promise.resolve(null)),
    CAPACITY_POLL_MS,
    [id, expert.on],
  )
  // 임대 디스크 순서 편집(B-26): 저장 중인 순서만 잠시 들고, 답이 오면 원장을 다시 읽는다.
  const [orderBusy, setOrderBusy] = useState(false)
  const [orderError, setOrderError] = useState<unknown>(null)
  const reorder = async (index: number, delta: -1 | 1) => {
    const current = capacity.data?.lease_storage_order
    if (!id || !current) return
    const next = moveTier(current, index, delta)
    if (next === current) return
    setOrderBusy(true)
    setOrderError(null)
    try {
      await api.setStorageOrder(id, next)
      capacity.refresh()
    } catch (error) {
      setOrderError(error)
    } finally {
      setOrderBusy(false)
    }
  }
  const validations = usePoll(
    () => (id ? api.validations(id) : Promise.resolve([])),
    useFallback(VALIDATION_POLL_MS),
    [id],
  )
  const membershipRead = usePoll(
    () => (id ? api.clusterMembership(id) : Promise.resolve(null)),
    useFallback(MEMBERSHIP_POLL_MS),
    [id],
  )
  // 이 노드 위의 VM — 산 게스트가 이 노드에 있는 임대를 서버가 골라 준다. VM 의 상태·시작·팀은 그 임대가 싣고 온다.
  // 임대를 싣는 읽기라 임대·VM·이름 변경 알림에 다시 읽고, 할당 해제 같은 쓰기 뒤에도 다시 읽는다.
  const held = usePoll(() => (id ? leaseApi.list({ node: id }) : Promise.resolve<Lease[]>([])), useFallback(HELD_POLL_MS), [id])
  useRefetchOn(leaseChanged, held.refresh, 1000)
  useRefreshAfterWrites(held.refresh)
  // 이 노드의 점검·검증이 움직이면 점검을, 상태·소속이 바뀌면 소속 화면을 다시 읽는다.
  useRefetchOn(
    (c) => c.kind === 'node' && c.id === id && (c.what === 'validation' || c.what === 'verify'),
    validations.refresh,
  )
  useRefetchOn(
    (c) => c.kind === 'node' && c.id === id && (c.what === 'membership' || c.what === 'state'),
    membershipRead.refresh,
  )
  // 다음 부팅에 포함할 GPU(F-71·#427, #486): GPU 줄마다 고급의 체크박스. 고른 것은 화면의 것이고,
  // 손대지 않은 카드는 지금의 정책을 비춘다 — all 이면 체크, none 이면 빈칸, selected 면 고른 카드만.
  const [gpuPicks, setGpuPicks] = useState<Record<string, boolean>>({})
  const [gpuTestBusy, setGpuTestBusy] = useState(false)
  const [gpuTestError, setGpuTestError] = useState<unknown>(null)
  const [gpuTestAsked, setGpuTestAsked] = useState(false)
  // 호스트 화면 없이 운영(#615) — 고른 값은 화면의 것이고, 손대지 않으면 노드의 값이다.
  const [headlessPick, setHeadlessPick] = useState<boolean | null>(null)
  const [resumeOpen, setResumeOpen] = useState(false)
  const [resumeBusy, setResumeBusy] = useState(false)
  const [resumeError, setResumeError] = useState<unknown>(null)
  const [resume, setResume] = useState<MaintenanceResume | null>(null)
  const [clusterAction, setClusterAction] = useState<DecisionMode | 'leave' | null>(null)
  const [clusterBusy, setClusterBusy] = useState(false)
  const [clusterError, setClusterError] = useState<unknown>(null)
  const [picked, setPicked] = useState<string[]>([])
  const [releaseOpen, setReleaseOpen] = useState(false)
  const [releaseReason, setReleaseReason] = useState('')
  const [releaseBusy, setReleaseBusy] = useState(false)
  const [releaseError, setReleaseError] = useState<unknown>(null)
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameText, setRenameText] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)
  const [renameError, setRenameError] = useState<unknown>(null)
  const [renamedTo, setRenamedTo] = useState<string | null>(null)

  if (!row) return <Empty>없는 노드</Empty>
  const n = row.node
  const fault = unreportedWord(n)
  const netFault = clusterNetFaultWord(n)
  const cluster = world.clusters.find((c) => c.id === n.cluster_id)
  const membership = membershipRead.data
  // 그래프는 클러스터에 연결된 뒤에만 있다 — 그 전에는 읽을 클러스터가 없다.
  const connected = membership ? membership.state === 'member' : Boolean(n.cluster_id)
  const currentCluster = membership ? membership.current_cluster : cluster
  const displayName = renamedTo ?? (membership?.display_name.trim() || row.name)
  const rename = async () => {
    const name = renameText.trim()
    if (!name || !id) return
    setRenameBusy(true)
    setRenameError(null)
    try {
      const updated = await api.rename(id, name)
      setRenamedTo(updated.display_name)
      setRenameOpen(false)
    } catch (error) {
      setRenameError(error)
    } finally {
      setRenameBusy(false)
    }
  }
  // 경로의 형제: 같은 클러스터의 다른 노드(미연결이면 모든 노드).
  const nodeSiblings = world.nodes
    .filter((r) => r.node.id !== n.id && (!n.cluster_id || r.node.cluster_id === n.cluster_id))
    .map((r) => ({ label: r.name, to: `/nodes/${r.node.id}` }))
  const onNode = (held.data ?? []).filter((l) => l.vm && isLive(l.vm) && l.vm.node_id === n.id)
  const vms = onNode.map((l) => l.vm as Vm)
  const leaseOf = (v: Vm): Lease | undefined => onNode.find((l) => l.id === v.lease_id)
  // 고를 수 있는 것은 활성 임대뿐이다. 반납 중·승인 대기는 해제할 것이 없다.
  const releasable = vms.filter((v) => leaseOf(v)?.state === 'active')
  // GPU 그래프의 선은 이 노드의 GPU 중 살아 있는 VM 에 붙은 것들이다 — VM 이 아니라 GPU 가 키다.
  const gpuLines: GpuLine[] = []
  for (const v of vms) {
    for (const g of v.gpus) {
      if (gpuLines.some((l) => l.id === g.gpu_id)) continue
      const own = n.gpus.find((x) => x.id === g.gpu_id)
      const model = own?.model ?? g.model ?? 'GPU'
      const slot = gpuSlot(own?.pci_addr ?? g.pci_addr)
      gpuLines.push({ id: g.gpu_id, label: slot ? `${model} · ${slot}` : model })
    }
  }
  const chosen = releasable.filter((v) => picked.includes(v.id))
  const allChosen = releasable.length > 0 && chosen.length === releasable.length
  const chosenTotal = chosen.reduce(
    (acc, v) => ({ cores: acc.cores + v.cores, memory_mb: acc.memory_mb + v.memory_mb, gpus: acc.gpus + v.gpus.length }),
    { cores: 0, memory_mb: 0, gpus: 0 },
  )
  const togglePick = (id: string, on: boolean) =>
    setPicked((prev) => (on ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)))

  const release = async () => {
    setReleaseBusy(true)
    setReleaseError(null)
    const done: string[] = []
    let firstError: unknown = null
    for (const v of chosen) {
      try {
        await leaseApi.end(v.lease_id, releaseReason.trim() || undefined)
        done.push(v.id)
      } catch (error) {
        if (firstError === null) firstError = error
      }
    }
    setPicked((prev) => prev.filter((id) => !done.includes(id)))
    setReleaseBusy(false)
    if (done.length > 0) held.refresh()
    if (firstError !== null) {
      setReleaseError(firstError)
      return
    }
    setReleaseReason('')
    setReleaseOpen(false)
  }
  const res = n.resources
  const validation = validations.data?.[0] ?? null
  const attempt = validation ? latestAttempt(validation) : null
  const failedChecks = attempt?.checks.filter((check) => check.state === 'failed') ?? []
  const excluded = n.excluded_gpus ?? []
  const progressRow = progressTrail(n)
  const gpuInPolicy = (addr: string) =>
    n.gpu_bind_policy === 'all' ? true : n.gpu_bind_policy === 'none' ? false : (n.gpu_bind_selected ?? []).includes(addr)
  const gpuPicked = (addr: string) => gpuPicks[addr] ?? gpuInPolicy(addr)
  const gpuChosen = n.gpus.map((g) => g.pci_addr).filter(gpuPicked)
  const headless = headlessPick ?? n.gpu_headless === true
  const headlessDirty = headless !== (n.gpu_headless === true)
  // 화면 칸은 그 칸이 뜻을 갖는 노드에만 선다 — 화면 때문에 빠진 카드가 있거나 이미 포기한 노드.
  const headlessShown = n.gpu_headless === true || n.gpus.some((g) => g.exclusion === 'boot_vga_console')
  const gpuDirty = n.gpus.some((g) => gpuPicked(g.pci_addr) !== gpuInPolicy(g.pci_addr)) || headlessDirty
  const gpuTestPending = n.gpu_test_pending === true || gpuTestAsked
  const scheduleGpuTest = async () => {
    if (!id || !gpuDirty) return
    setGpuTestBusy(true)
    setGpuTestError(null)
    try {
      // 모두면 all, 하나도 없으면 none, 그 사이만 목록이다 — API 의 세 모양 그대로.
      const body: NodeGpuTest =
        gpuChosen.length === n.gpus.length
          ? { policy: 'all' as const }
          : gpuChosen.length === 0
            ? { policy: 'none' as const }
            : { policy: 'selected' as const, gpus: gpuChosen }
      if (headlessDirty) body.headless = headless
      await api.gpuTest(id, body)
      setGpuPicks({})
      setHeadlessPick(null)
      setGpuTestAsked(true)
    } catch (error) {
      setGpuTestError(error)
    } finally {
      setGpuTestBusy(false)
    }
  }
  const locallyRequestedAt =
    resume?.node_id === n.id && n.maintenance_resume_allowed ? resume.requested_at : null
  const resumeRequestedAt = n.maintenance_resume_requested_at ?? locallyRequestedAt

  const requestResume = async () => {
    if (!id) return
    setResumeBusy(true)
    setResumeError(null)
    try {
      setResume(await api.resumeAfterMaintenance(id))
      setResumeOpen(false)
    } catch (error) {
      setResumeError(error)
    } finally {
      setResumeBusy(false)
    }
  }

  const openClusterAction = (action: DecisionMode | 'leave') => {
    setClusterError(null)
    setClusterAction(action)
  }

  const requestClusterAction = async () => {
    if (!id || !clusterAction) return
    setClusterBusy(true)
    setClusterError(null)
    try {
      if (clusterAction === 'leave') await api.leaveCluster(id)
      setClusterAction(null)
      membershipRead.refresh()
    } catch (error) {
      setClusterError(error)
    } finally {
      setClusterBusy(false)
    }
  }

  return (
    <>
      <Crumb
        steps={
          currentCluster
            ? [
                { label: '클러스터', to: '/clusters' },
                { label: currentCluster.name, to: '/clusters' },
                { label: displayName, siblings: nodeSiblings },
              ]
            : [
                { label: '클러스터', to: '/clusters' },
                { label: '클러스터 미연결', to: '/clusters' },
                { label: displayName, siblings: nodeSiblings },
              ]
        }
      />
      <PageHead
        icon={<Server className="size-5" />}
        kind="노드"
        title={displayName}
        right={
          // VM 상세 제목 줄과 같은 두 줄 — 위는 콘솔 칩과 상태, 아래는 조작 칩. 고급의 칩은 좁은 화면에 없다
          // (VM 의 콘솔과 같은 이유: 셸도 비밀번호 대화 상자도 폰의 일이 아니다).
          <div className="mt-1 flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              {expert.on && n.online ? <NodeShellChip nodeId={n.id} name={displayName} className={`${CHIP} max-sm:hidden`} /> : null}
              <IommuWarning failedChecks={n.failed_checks} />
              {row.status === 'offline' ? <NodeRemoveBadge node={n} name={displayName} onRemoved={() => navigate('/clusters')} /> : <StatusBadge status={row.status} />}
            </div>
            <div className="flex gap-1.5">
              <button type="button" className={CHIP} onClick={() => { setRenameText(displayName); setRenameError(null); setRenameOpen(true) }}>
                이름 바꾸기
              </button>
              {expert.on ? <RootPasswordChip nodeId={n.id} className={`${CHIP} max-sm:hidden`} /> : null}
              {expert.on && n.online ? <NodeRebootChip nodeId={n.id} className={`${CHIP_ACTION} ${ACTION_DANGER} max-sm:hidden`} /> : null}
            </div>
          </div>
        }
        facts={
          <>
            {/* 클러스터가 보는 노드라도 보고가 끊긴 것은 '마지막 확인 방금' 이 아니라 고장이다(#503). */}
            {fault ?? (
              <>
                {row.status === 'preparing' ? <>{stage(n)} · </> : null}
                마지막 확인 <Ago at={n.last_seen_at} />
              </>
            )}
            {/* 노드가 말한 VM 망의 고장(D-7) — 닿지 못함은 건너뛸 범주가 아니라 보여야 하는 고장이다. */}
            {netFault ? <div className="mt-1 text-danger-ink">{netFault}</div> : null}
            <ExpertLine
              className="mt-1"
              items={[
                n.pve_node_name ?? DASH,
                row.status === 'offline' ? DASH : (n.mgmt_ip ?? DASH),
                pveVersion(n),
                `마지막 확인 ${exact(n.last_seen_at)}`,
                n.reboot_unreported_since ? `재부팅 미보고 ${exact(n.reboot_unreported_since)}부터` : null,
                n.agent_silent_since ? `보고 끊김 ${exact(n.agent_silent_since)}부터` : null,
                n.agent_base_url ? `노드 주소 ${n.agent_base_url}` : null,
                n.units_settled === false ? `서비스 설정 미적용 ${exact(n.units_unsettled_since)}부터` : null,
                n.cluster_net_fault
                  ? `VM 망 고장 ${exact(n.cluster_net_fault.since)}부터${n.cluster_net_fault.unreachable_peers.length ? ` · ${n.cluster_net_fault.unreachable_peers.join(', ')}` : ''}`
                  : null,
                n.schedulable ? '배치 가능' : '배치 제외',
                n.pdu ? `콘센트 ${n.pdu.pdu_id} · ${n.pdu.outlet}번` : null,
              ]}
            />
          </>
        }
      />

      <div className="mt-6 rounded-lg border bg-card px-4 py-3">
        <Err error={membershipRead.error} />
        <Err error={validations.error} />
        <div className={connected ? 'grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(320px,44%)]' : ''}>
        <div className="min-w-0">
        <Facts
          rows={[
            ...(progressRow ? [progressRow] : []),
            [
              '클러스터',
              membership ? (
                <div key="cluster" className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                    {/* 연결된 노드는 클러스터 이름만 — `연결됨` 배지는 오프라인 노드에서도 서서 살아 있다고 오인하게 했다. 진행 중인 소속 변경만 낱말로 보인다. */}
                    {membership.state === 'member' ? null : <ToneBadge tone="none">{membershipWord(membership.state, membership.decision)}</ToneBadge>}
                    {membership.current_cluster ? (
                      <Link to="/clusters" className="hover:underline">
                        {membership.current_cluster.name}
                      </Link>
                    ) : (
                      <span>미연결</span>
                    )}
                    {membership.decision ? <span className="text-muted-foreground">{decisionText(membership.decision)}</span> : null}
                    {n.stale_cluster ? (
                      <span className="text-muted-foreground">
                        <span>이전 클러스터 </span>
                        <span>{`${n.stale_cluster.name} 정리 중`}</span>
                      </span>
                    ) : null}
                  </div>
                  {membership.actions.length > 0 ? (
                    <div className="flex shrink-0 gap-1.5">
                      {membership.actions.includes('decide') ? (
                        <>
                          <Button size="sm" variant="outline" className={ACTION} onClick={() => openClusterAction('create')}>클러스터 생성</Button>
                          <Button size="sm" className={ACTION} onClick={() => openClusterAction('connect')}>클러스터 연결</Button>
                        </>
                      ) : null}
                      {membership.actions.includes('leave') ? (
                        <Button size="sm" variant="outline" className={ACTION} onClick={() => openClusterAction('leave')}>연결 해제</Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : membershipRead.loading ? (
                '불러오는 중'
              ) : membershipRead.error ? (
                DASH
              ) : (
                '소속 정보 없음'
              ),
            ],
            ['CPU', res.reachable && res.cpu ? `${res.cpu.threads}코어 · 사용량 ${Math.round(res.cpu.usage * 100)}%` : DASH],
            ['메모리', res.reachable && res.memory ? sizeUsage(res.memory.used_mb / 1024, res.memory.total_mb / 1024) : DASH],
            [
              '스토리지',
              res.reachable && res.storages.every((storage) => storage.status === 'available')
                ? storageText(n)
                : DASH,
            ],
            [
              'GPU',
              n.gpus.length === 0 ? (
                '없음'
              ) : (
                <div key="gpus" className="min-w-0">
                  <div>{`${res.gpus.free} / ${res.gpus.total}장 사용 가능`}</div>
                  {/* 열 하나를 minmax(0,1fr) 로 눌러 둔다 — auto 트랙은 고급의 nowrap 보조 줄이 끌어 dd 밖으로 넓어진다(#464). */}
                  <div className="mt-1.5 grid grid-cols-[minmax(0,1fr)] gap-1">
                    {n.gpus.map((g) => (
                      <div key={g.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        {expert.on ? (
                          <Checkbox
                            aria-label={`${g.pci_addr} 다음 부팅에 포함`}
                            checked={gpuPicked(g.pci_addr)}
                            disabled={gpuTestBusy}
                            onCheckedChange={(checked) => setGpuPicks((prev) => ({ ...prev, [g.pci_addr]: checked === true }))}
                          />
                        ) : null}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{g.model ?? DASH}</span>
                          <ExpertLine
                            mono
                            items={[
                              // 연결 낱말이 앞이다 — 줄이 넘치면 뒤가 말줄임으로 잘리고, 잘려도 되는 것은 식별자다.
                              gpuBindWord(g.bind),
                              g.pci_addr,
                              g.iommu_group !== null ? `IOMMU ${g.iommu_group}` : null,
                              g.pve_mapping_id ? `매핑 ${g.pve_mapping_id}` : null,
                            ]}
                          />
                        </span>
                        <span className="w-16 text-right text-muted-foreground max-sm:w-auto max-sm:text-left">{g.vram_mb ? sizeMb(g.vram_mb) : ''}</span>
                        <Badge variant="outline" className={GPU_BADGE[gpuWord(n, g).tone]}>
                          {gpuWord(n, g).word}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  {expert.on && headlessShown ? (
                    <label className="mt-2 flex items-center gap-2">
                      <Checkbox checked={headless} disabled={gpuTestBusy} onCheckedChange={(checked) => setHeadlessPick(checked === true)} />
                      <span>호스트 화면 없이 운영</span>
                    </label>
                  ) : null}
                  {expert.on && (gpuDirty || gpuTestError) ? (
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <Button size="sm" className={`${ACTION} bg-brand hover:bg-brand-hover`} disabled={gpuTestBusy || !gpuDirty} onClick={() => void scheduleGpuTest()}>
                        다음 부팅에 저장
                      </Button>
                      <Err error={gpuTestError} />
                    </div>
                  ) : null}
                  {gpuTestPending && !gpuDirty ? (
                    <div className="mt-1.5 text-muted-foreground">다음 부팅에서 고른 GPU 만 연결합니다. 노드를 재부팅하세요.</div>
                  ) : n.gpu_bind_policy === 'none' && !gpuDirty ? (
                    <div className="mt-1.5 text-muted-foreground">GPU 를 연결하지 않고 부팅합니다. 시험할 GPU 를 고르세요.</div>
                  ) : null}
                </div>
              ),
            ],
            [
              '장비',
              <div key="device" className="min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                  {resumeRequestedAt ? <ToneBadge tone="none">복귀 대기</ToneBadge> : null}
                  {validation && !validation.finished_at ? (
                    <span>{validationStepWord(validation.current_step)}</span>
                  ) : validation ? (
                    <span>최근 점검 <Ago at={validation.finished_at ?? validation.created_at} /></span>
                  ) : resumeRequestedAt ? null : validations.loading ? (
                    <span className="text-muted-foreground">불러오는 중</span>
                  ) : validations.error ? null : (
                    <span className="text-muted-foreground">점검 결과 없음</span>
                  )}
                  </div>
                  {n.maintenance_resume_allowed && !resumeRequestedAt ? (
                    <Button
                      size="sm"
                      className={`${ACTION} shrink-0 bg-brand hover:bg-brand-hover`}
                      onClick={() => {
                        setResumeError(null)
                        setResumeOpen(true)
                      }}
                    >
                      운영 복귀 예약
                    </Button>
                  ) : null}
                </div>
                {n.install_rejected ? (
                  <div className="mt-1.5 grid gap-0.5 sm:grid-cols-[9rem_1fr] sm:gap-3">
                    <div className="font-medium">설치</div>
                    <div className="text-muted-foreground">
                      {n.install_rejected === 'boot_disks_multiple'
                        ? '부팅 디스크가 여럿으로 잡혔습니다. 프로필의 디스크 필터를 고치고 다시 설치하세요.'
                        : n.install_rejected === 'disk_report_missing'
                          ? '설치 매체가 디스크를 보고하지 않았습니다. 새 ISO 로 다시 설치하세요.'
                          : '이 기종의 설치 프로필이 없습니다. 프로필에 기종을 지정하고 다시 설치하세요.'}
                    </div>
                  </div>
                ) : null}
                {excluded.length > 0 ? (
                  <div className="mt-1.5 grid gap-1">
                    {excluded.map((addr) => (
                      <div key={addr} className="grid gap-0.5 sm:grid-cols-[9rem_1fr] sm:gap-3">
                        <div className="font-medium">제외된 GPU</div>
                        <div className="text-muted-foreground">
                          이 GPU 를 쓰는 동안 장비가 응답하지 않았습니다. 그 GPU 를 교체하세요. · {addr}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                {failedChecks.length > 0 ? (
                  <div className="mt-1.5 grid gap-1">
                    {failedChecks.map((check, index) => {
                      const description = validationCheckDescriptionWord(check.code)
                      const observed = description === DASH ? null : validationObservedWord(check.code, check.observed)
                      return (
                        <div key={`${check.code}-${index}`} className="grid gap-0.5 sm:grid-cols-[9rem_1fr] sm:gap-3">
                          <div className="font-medium">{validationCheckWord(check.code)}</div>
                          <div className="text-muted-foreground">
                            {description}
                            {observed ? ` · ${observed}` : ''}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
              </div>,
            ],
            [
              '검증',
              n.last_verify ? (
                <div key="verify" className="grid min-w-0 gap-1">
                  {verifyRows(n.last_verify).map((row) => (
                    <div key={row.step} className="grid gap-0.5 sm:grid-cols-[9rem_1fr] sm:gap-3">
                      <div className="font-medium">{verifyStepWord(row.step)}</div>
                      <div className={row.failed ? '' : 'text-muted-foreground'}>
                        {row.result}
                        {row.items.length > 0 ? <ExpertLine mono items={row.items} /> : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                DASH
              ),
            ],
          ]}
          expert={[
            ['등록 방식', nodeOriginWord(n.origin)],
            ['기종', n.dmi_system_name ?? DASH],
            ...(detail.data?.image_copies?.length
              ? ([
                  [
                    '이미지',
                    <div key="images">
                      {detail.data.image_copies.map((c) => (
                        <div key={c.source_key}>{imageCopyText(c)}</div>
                      ))}
                    </div>,
                  ],
                ] as FactRow[])
              : []),
            ...(n.last_verify
              ? ([
                  ['검증 시작', exact(n.last_verify.started_at)],
                  ['검증 끝', exact(n.last_verify.finished_at)],
                  ['검증 기록', <span key="verify-id" className="font-mono">{n.last_verify.id}</span>],
                ] as FactRow[])
              : []),
            // 끊긴 push 의 재시도(installed)는 서비스가 아직 막는다(#415) — 열리면 여기에 더한다.
            ...(n.origin === 'adopted' && (n.state === 'maintenance' || n.state === 'onboarding_failed')
              ? ([['편입', <NodeOnboardRow key="onboard" nodeId={n.id} />]] as FactRow[])
              : []),
            ...(cluster
              ? ([
                  ['가상화 클러스터 이름', cluster.pve_cluster_name ?? DASH],
                  ['클러스터 등록', clusterOriginWord(cluster.origin)],
                  ['클러스터 멤버', liveText(cluster)],
                  ['클러스터 확인', exact(cluster.last_member_seen_at)],
                ] as FactRow[])
              : []),
            ...(validation
              ? ([
                  ['점검 범위', validationScopeWord(validation.scope)],
                  ['점검 시도', `${validation.attempts.length} / ${validation.max_attempts}회`],
                  ['다음 시도', validation.next_attempt_at ? exact(validation.next_attempt_at) : DASH],
                  ['점검 시작', exact(attempt?.started_at ?? validation.created_at)],
                  ['점검 끝', exact(validation.finished_at)],
                ] as FactRow[])
              : []),
            ...(res.reachable
              ? res.storages.map(
                  (s): FactRow => [
                    `볼륨 ${s.name}`,
                    `${sizeUsage(s.used_mb / 1024, s.total_mb / 1024)} · ${s.shared ? '공유' : '로컬'} · ${s.status === 'available' ? '사용 가능' : '사용 불가'}`,
                  ],
                )
              : []),
            // 지난 부팅(A6-16): 판정 한 낱말, 그리고 갑자기 멈춘 부팅은 그 마지막 줄들.
            ...(boots.data ?? []).map((b, i): FactRow => [i === 0 ? '지난 부팅' : ' ', <BootLogRow key={b.boot_id} log={b} />]),
            // 크래시 기록(#412): 요약 한 줄, 누르면 전문. 없으면 행이 없다.
            ...(crashes.data ?? []).map((c, i): FactRow => [
              i === 0 ? '크래시 기록' : ' ',
              <CrashRecordRow key={c.id ?? c.record_key} record={c} />,
            ]),
            ...(installs.data ?? []).map(
              (r, i): FactRow => [i === 0 ? '설치 기록' : ' ', <InstallerRunRow key={r.installer_boot_id} node={id ?? ''} run={r} />],
            ),
            // 디스크(A6-12): 설치기가 본 디스크마다 종류·크기·건강과 풀 또는 제외 사유.
            ...(disks.data?.disks ?? []).map((d): FactRow => [`디스크 ${d.devname}`, diskText(d, disks.data?.pools)]),
            ...(disks.data?.pools ?? []).map(
              (p): FactRow => [
                `데이터 풀 ${p.storage_name}`,
                `${tierWord(p.class)}${p.kind === 'lvmthin' ? ' · LVM' : ''} · ${p.devnames.length}개 · ${p.registered ? '등록됨' : '등록 대기'}`,
              ],
            ),
            ...(capacity.data
              ? ([
                  ['할당', amountOf(capacity.data.allocated, capacity.data.total)],
                  ['호스트 예약', amount(capacity.data.reserve)],
                  ['사용량', capacity.data.used ? amountOf(capacity.data.used, capacity.data.total) : DASH],
                  // 단계별 할당/전체(B-24): 순서의 항목마다 한 줄.
                  ...capacity.data.tiers.map((t): FactRow => [`단계 ${tierWord(t.tier)}`, tierText(t)]),
                  [
                    '임대 디스크 순서',
                    <TierOrder
                      key="order"
                      order={capacity.data.lease_storage_order}
                      busy={orderBusy}
                      onMove={reorder}
                    />,
                  ],
                ] as FactRow[])
              : []),
          ]}
        />
        {expert.on ? <Err error={boots.error} /> : null}
        {expert.on ? <Err error={detail.error} /> : null}
        {expert.on ? <Err error={capacity.error} /> : null}
        {expert.on ? <Err error={disks.error} /> : null}
        {expert.on ? <Err error={orderError} /> : null}
        </div>
        {connected ? <NodeUsageCharts nodeId={n.id} gpus={gpuLines} /> : null}
        </div>
      </div>

      <Section title="할당된 VM">
        {vms.length === 0 ? (
          <Empty>
            <MonitorSmartphone className="size-4" /> {held.loading ? '불러오는 중' : 'VM 없음'}
          </Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            {chosen.length > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-surface-2 px-4 py-2 text-[12px]">
                <span>
                  <b className="font-medium">{chosen.length}대 선택</b> · 합계 {chosenTotal.cores}코어 · {sizeMb(chosenTotal.memory_mb)} · GPU {chosenTotal.gpus}장
                </span>
                <Button size="sm" variant="destructive" onClick={() => { setReleaseError(null); setReleaseOpen(true) }}>
                  할당 해제
                </Button>
              </div>
            ) : null}
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {/* 다중 선택은 넓은 화면의 것이다 — 고를 수 없으면 선택 요약 바와 `할당 해제` 가 서지 않는다. */}
                  <TableHead className="w-10 max-sm:hidden">
                    <Checkbox
                      aria-label="전체 선택"
                      disabled={releasable.length === 0}
                      checked={allChosen ? true : chosen.length > 0 ? 'indeterminate' : false}
                      onCheckedChange={(on) => setPicked(on === true ? releasable.map((v) => v.id) : [])}
                    />
                  </TableHead>
                  <TableHead>VM</TableHead>
                  <TableHead className="w-56">스펙</TableHead>
                  <TableHead className="w-32">사용량</TableHead>
                  <TableHead className="w-36">사용자</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vms.map((v) => {
                  const lease = leaseOf(v)
                  const canPick = lease?.state === 'active'
                  return (
                    <TableRow key={v.id} data-state={picked.includes(v.id) && canPick ? 'selected' : undefined} className="hover:bg-surface-2">
                      <TableCell className="max-sm:hidden">
                        <Checkbox
                          aria-label={`${v.name} 선택`}
                          disabled={!canPick}
                          checked={canPick && picked.includes(v.id)}
                          onCheckedChange={(on) => togglePick(v.id, on === true)}
                        />
                      </TableCell>
                      <TableCell className="font-medium max-sm:basis-full">
                        <div className="flex items-center gap-2">
                          <Link to={`/vms/${v.lease_id}`} className="hover:underline">
                            {v.name}
                          </Link>
                          {lease && lease.state !== 'active' ? (
                            <ToneBadge tone={leaseToneOf(lease.state)}>{leaseWord(lease.state)}</ToneBadge>
                          ) : (
                            <ToneBadge tone={vmToneOf(v.state)}>{vmWord(v.state)}</ToneBadge>
                          )}
                        </div>
                        <ExpertLine items={[`VMID ${v.vmid}`, v.pool ? `풀 ${v.pool}` : null]} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{vmSpecText(v)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {lease?.started_at ? <>점유 <Duration at={lease.started_at} /></> : DASH}
                        <ExpertLine items={[lease?.started_at ? `시작 ${exact(lease.started_at)}` : null]} />
                      </TableCell>
                      <TableCell>
                        {lease?.team ? (
                          <>
                            팀 {lease.team.name}
                            <div className="text-[11px] text-muted-foreground">{ownerLabel(v.owner)}</div>
                          </>
                        ) : (
                          ownerLabel(v.owner)
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <Dialog open={renameOpen} onOpenChange={(open) => (renameBusy ? undefined : setRenameOpen(open))}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>이름 바꾸기</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="node-rename">이름</Label>
            <Input id="node-rename" maxLength={120} value={renameText} onChange={(event) => setRenameText(event.target.value)} />
          </div>
          <Err error={renameError} />
          <DialogFooter>
            <Button variant="outline" disabled={renameBusy} onClick={() => setRenameOpen(false)}>취소</Button>
            <Button disabled={renameBusy || !renameText.trim()} onClick={() => void rename()}>
              {renameBusy ? '저장 중' : '저장'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={releaseOpen} onOpenChange={(open) => (releaseBusy ? undefined : setReleaseOpen(open))}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>할당 해제</DialogTitle>
          </DialogHeader>
          <ul className="grid gap-1 text-[13px]">
            {chosen.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-3">
                <span className="font-medium">{v.name}</span>
                <span className="text-muted-foreground">{leaseOf(v)?.team ? `팀 ${leaseOf(v)?.team?.name}` : ownerLabel(v.owner)}</span>
              </li>
            ))}
          </ul>
          <div className="grid gap-2">
            <Label htmlFor="release-reason">사유</Label>
            <Input id="release-reason" maxLength={200} value={releaseReason} onChange={(event) => setReleaseReason(event.target.value)} />
          </div>
          <Err error={releaseError} />
          <DialogFooter>
            <Button variant="outline" disabled={releaseBusy} onClick={() => setReleaseOpen(false)}>취소</Button>
            <Button variant="destructive" disabled={releaseBusy || chosen.length === 0} onClick={() => void release()}>
              {releaseBusy ? '해제 중' : '해제'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClusterDecisionDialog
        mode={clusterAction === 'connect' ? 'connect' : 'create'}
        open={clusterAction === 'create' || clusterAction === 'connect'}
        nodeId={n.id}
        candidates={membership?.candidates ?? []}
        onClose={() => setClusterAction(null)}
        onDecided={() => {
          setClusterAction(null)
          membershipRead.refresh()
        }}
      />

      <Dialog open={clusterAction === 'leave'} onOpenChange={(open) => (!open && !clusterBusy ? setClusterAction(null) : undefined)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>연결 해제</DialogTitle>
          </DialogHeader>
          <Err error={clusterError} />
          <DialogFooter>
            <Button variant="outline" disabled={clusterBusy} onClick={() => setClusterAction(null)}>취소</Button>
            <Button variant="destructive" disabled={clusterBusy} onClick={() => void requestClusterAction()}>
              {clusterBusy ? '처리 중' : '해제'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resumeOpen} onOpenChange={(open) => (resumeBusy ? undefined : setResumeOpen(open))}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>운영 복귀 예약</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground">다음 부팅 점검을 통과하면 배치에 다시 들어갑니다.</p>
          <Err error={resumeError} />
          <DialogFooter>
            <Button variant="outline" disabled={resumeBusy} onClick={() => setResumeOpen(false)}>
              취소
            </Button>
            <Button disabled={resumeBusy} className="bg-brand hover:bg-brand-hover" onClick={() => void requestResume()}>
              {resumeBusy ? '예약 중' : '예약'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function latestAttempt(validation: NodeValidation): NodeValidationAttempt | null {
  return validation.attempts.reduce<NodeValidationAttempt | null>(
    (latest, attempt) => (!latest || attempt.attempt > latest.attempt ? attempt : latest),
    null,
  )
}

function storageText(n: ApiNode): string {
  const s = n.resources.storages
  if (s.length === 0) return DASH
  const used = s.reduce((a, x) => a + x.used_mb, 0)
  const total = s.reduce((a, x) => a + x.total_mb, 0)
  return `${sizeUsage(used / 1024, total / 1024)} · ${s.length}개`
}

function pveVersion(n: ApiNode): string | null {
  const v = n.pve_version?.replace(/^pve-manager\//, '').split('/')[0]
  return v ? v : null
}

/** 임대 디스크 순서의 칩과 위/아래 버튼(B-26). 첫 여유 있는 단계가 디스크를 받으므로 앞이 우선이다. */
function TierOrder({ order, busy, onMove }: { order: StorageTier[]; busy: boolean; onMove: (index: number, delta: -1 | 1) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {order.map((tier, index) => (
        <span key={tier} className="inline-flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 text-[11px]">
          <span>{tierWord(tier)}</span>
          <button
            type="button"
            aria-label={`${tierWord(tier)} 앞으로`}
            className="px-0.5 text-muted-foreground hover:text-foreground disabled:opacity-40"
            disabled={busy || index === 0}
            onClick={() => onMove(index, -1)}
          >
            ▲
          </button>
          <button
            type="button"
            aria-label={`${tierWord(tier)} 뒤로`}
            className="px-0.5 text-muted-foreground hover:text-foreground disabled:opacity-40"
            disabled={busy || index === order.length - 1}
            onClick={() => onMove(index, 1)}
          >
            ▼
          </button>
        </span>
      ))}
    </div>
  )
}

function amount(a: { cores: number; memory_mb: number; disk_gb: number }): string {
  const cores = Number.isInteger(a.cores) ? String(a.cores) : a.cores.toFixed(1)
  return `${cores}코어 · ${sizeMb(a.memory_mb)} · ${size(a.disk_gb)}`
}

/** 세 축의 `쓴 양 / 전체` 한 줄 — `12 / 16코어 · 64 / 256GB · 1.2 / 4.0TB`. 전체를 모르면 각 축의 전체가 `—` 다. */
function amountOf(a: { cores: number; memory_mb: number; disk_gb: number }, total: { cores: number; memory_mb: number; disk_gb: number } | null | undefined): string {
  const cores = Number.isInteger(a.cores) ? String(a.cores) : a.cores.toFixed(1)
  return [
    `${cores} / ${total ? total.cores : DASH}코어`,
    sizeUsage(a.memory_mb / 1024, total ? total.memory_mb / 1024 : null),
    sizeUsage(a.disk_gb, total ? total.disk_gb : null),
  ].join(' · ')
}

/** 기록된 결정 한 줄: 어느 클러스터에 참여하는지, 어떤 이름으로 만드는지. */
function decisionText(d: NodeClusterDecision): string {
  if (d.action === 'join') return `${d.cluster?.name ?? DASH} 에 연결`
  return d.name ? `${d.name} 생성` : '새 클러스터 생성'
}
